import React, { FunctionComponent, useCallback, useState } from "react";
import "./InputNumber.css";

interface InputNumberProps {
  minimumValue?: number;
  maximumValue?: number;
  includePrefix?: boolean;
  includePostfix?: boolean;
  defaultValue?: number | null;
  onChange: (value: number) => void;
}

/**
 * Input for selecting a number, with optional controls for plus and minus.
 */
export const InputNumber: FunctionComponent<InputNumberProps> = ({
  includePrefix = true,
  includePostfix = true,
  minimumValue = 0,
  maximumValue = 31,
  defaultValue = 0,
  onChange,
}) => {
  const [value, setValue] = useState<number>(defaultValue ?? 0);

  const handleChangeText = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const valueUpdate = Number(event.target.value);
      if (isNaN(valueUpdate)) {
        return;
      }
      if (valueUpdate < minimumValue || valueUpdate > maximumValue) {
        return;
      }
      setValue(valueUpdate);
      onChange(valueUpdate);
    },
    [onChange, minimumValue, maximumValue],
  );

  const handleChangeValueByOne = (incrementAmount: number) => () => {
    const nextValue = value + incrementAmount;
    if (nextValue < minimumValue || nextValue > maximumValue) {
      return;
    }
    onChange(nextValue);
    setValue(nextValue);
  };

  return (
    <div className="input-number-component">
      <div className="container">
        {includePrefix && (
          <button
            type="button"
            onClick={handleChangeValueByOne(-1)}
            className="button">
            <svg
              width="12"
              height="2"
              viewBox="0 0 12 2"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M0 1h12" stroke="#595959" strokeWidth="2" />
            </svg>
          </button>
        )}
        <div className="textInputContainer">
          <input
            type="number"
            onChange={handleChangeText}
            value={value}
            className="input"
            min={minimumValue}
            max={maximumValue}
          />
        </div>
        {includePostfix && (
          <button
            type="button"
            onClick={handleChangeValueByOne(1)}
            className="button">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path d="M6 0v12M0 6h12" stroke="#595959" strokeWidth="2" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};
